import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useCurrentOrganization } from './use-current-organization';
import { useTranslation } from 'react-i18next';

export interface ProjectStatus {
  id: string;
  name: string;
  displayName?: string;
  /** Language-agnostic type: active, paused, completed, other */
  semanticType?: string;
  color?: string;
  isDefault?: boolean;
  organizationId: string;
  createdAt: string;
}

export function getProjectCardStatusTranslationKey(semanticType?: string | null): string | undefined {
  const normalizedSemantic = String(semanticType || '').toLowerCase().trim().replace('-', '_');
  if (normalizedSemantic === 'active' || normalizedSemantic === 'paused' || normalizedSemantic === 'completed') {
    return normalizedSemantic;
  }
  return undefined;
}

const COMPLETED_STATUS_NAME_FALLBACKS = ['completed', 'abgeschlossen', 'tamamlandı', 'erledigt'];
const ACTIVE_STATUS_NAME_FALLBACKS = ['active', 'aktiv'];

function findProjectStatusIdBySemantic(
  statuses: ProjectStatus[],
  semanticType: string,
): string | undefined {
  const normalized = semanticType.toLowerCase().trim();
  const bySemantic = statuses.find(
    (s) => (s.semanticType || '').toLowerCase().trim() === normalized,
  );
  if (bySemantic?.id) return bySemantic.id;

  const nameFallbacks =
    normalized === 'completed'
      ? COMPLETED_STATUS_NAME_FALLBACKS
      : normalized === 'active'
        ? ACTIVE_STATUS_NAME_FALLBACKS
        : [];
  if (nameFallbacks.length === 0) return undefined;

  const byName = statuses.find((s) =>
    nameFallbacks.includes((s.name || '').toLowerCase().trim()),
  );
  return byName?.id;
}

function isProjectStatusSemantic(
  statusId: string | null | undefined,
  statuses: ProjectStatus[],
  semanticType: string,
): boolean {
  if (!statusId) return false;
  const status = statuses.find((s) => s.id === statusId);
  if (!status) return false;
  const normalized = semanticType.toLowerCase().trim();
  if ((status.semanticType || '').toLowerCase().trim() === normalized) return true;
  const nameFallbacks =
    normalized === 'completed'
      ? COMPLETED_STATUS_NAME_FALLBACKS
      : normalized === 'active'
        ? ACTIVE_STATUS_NAME_FALLBACKS
        : [];
  return nameFallbacks.includes((status.name || '').toLowerCase().trim());
}

/** Sync project status with progress: 100% → completed; below 100% while completed → active. */
export function resolveProjectStatusIdForProgress(
  progress: number,
  currentStatusId: string | null | undefined,
  statuses: ProjectStatus[],
): string | null | undefined {
  if (!statuses.length) return currentStatusId;

  if (progress >= 100) {
    return findProjectStatusIdBySemantic(statuses, 'completed') ?? currentStatusId;
  }

  if (!isProjectStatusSemantic(currentStatusId, statuses, 'completed')) {
    return currentStatusId;
  }

  return findProjectStatusIdBySemantic(statuses, 'active') ?? currentStatusId;
}

/**
 * Hook to fetch project statuses for the current organization
 */
export function useProjectStatuses() {
  const { organizationId } = useCurrentOrganization();
  
  return useQuery<ProjectStatus[]>({
    queryKey: ["/api/project-statuses", organizationId],
    queryFn: async () => {
      const url = organizationId ? `/api/project-statuses?organizationId=${encodeURIComponent(organizationId)}` : "/api/project-statuses";
      const response = await apiRequest(url);
      const result = await response.json();
      return result?.data || [];
    },
    enabled: !!organizationId,
    staleTime: 30 * 60 * 1000, // 30 minutes - project statuses rarely change
    gcTime: 60 * 60 * 1000, // 60 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Respect staleTime cache - data will refresh when stale or after mutations
  });
}

/**
 * Hook to get project status display information (name, color, localized name)
 */
export function useProjectStatusDisplay(statusId: string | null | undefined) {
  const { data: statuses = [] } = useProjectStatuses();
  const { t } = useTranslation(['components.projectCard']);
  
  const status = statusId ? statuses.find(s => s.id === statusId) : null;
  
  const getDisplayName = (): string => {
    if (!status) return '-';

    const fallbackLabel = status.displayName || status.name;
    const translationKey = getProjectCardStatusTranslationKey(status.semanticType);
    if (translationKey) {
      return t(`components.projectCard:status.${translationKey}`, { defaultValue: fallbackLabel });
    }

    return fallbackLabel;
  };
  
  const getColor = (): string => {
    return status?.color || '#6b7280';
  };
  
  return {
    status,
    displayName: getDisplayName(),
    color: getColor(),
    isLoading: !statuses.length && !!statusId,
  };
}
